import { ErrorWarningIcon, Refresh, RoundCheckedIcon, pushToDataLayer, useToast } from '@arnold/common';
import { MD5 } from 'crypto-js';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClearAiSurveySummaryCacheMutation, type QuestionWithAnswer } from '../../../../generated/hooks';
import auth from '../../../../lib/auth';
import { AiSummaryLoading } from '../../../aiSummary/AiSummaryLoading';
import { prepareSummaryAnswers } from '../../../aiSummary/summaryInput';
import type { NewReport } from '../../hooks/useNewReport';
import type { ReportComponent } from '../../definition/types';
import { buildSummaryAnswers, hasAiSummaryData, parseSummaryPoints, SUMMARY_SENTIMENTS } from './summaryData';
import { SummaryPoints } from './SummaryPoints';
import { useSummaryStream } from './useSummaryStream';
import {
  AiBadge,
  Body,
  Card,
  CountBadge,
  Footer,
  Header,
  HeaderBody,
  Preview,
  RefreshButton,
  TitleRow,
  ToggleButton,
} from './styles';

type SummaryDisplayMode = Extract<ReportComponent, { type: 'aiSummary' }>['displayMode'];

export function AiSummary({
  report,
  editing,
  displayMode = 'collapsible',
}: {
  report: NewReport;
  editing: boolean;
  displayMode?: SummaryDisplayMode;
}) {
  const { t } = useTranslation('newReport');
  const [generation, setGeneration] = useState(0);
  const { rawData, currentGroup, languageCode } = report;
  const answers = useMemo(() => {
    const input = { rawData, currentGroup };
    return hasAiSummaryData(input) ? prepareSummaryAnswers(buildSummaryAnswers(input), languageCode) : [];
  }, [rawData, currentGroup, languageCode]);
  const scope = useMemo(
    () => MD5(JSON.stringify([report.accessToken, languageCode, answers])).toString(),
    [report.accessToken, languageCode, answers],
  );
  if (!answers.length) {
    // Keep an empty placement selectable/removable in the editor, but never request a summary of an empty cohort.
    return editing ? (
      <Card>
        <Body>
          {t('SurveySummary:title')} — {t('aiSummary.noData')}
        </Body>
      </Card>
    ) : null;
  }
  // Remount on changed authorized input or regeneration: stale chunks and pending timers cannot cross scopes.
  return (
    <SummaryContent
      key={`${scope}:${generation}`}
      alwaysExpanded={displayMode === 'expanded'}
      report={report}
      answers={answers}
      restart={() => setGeneration((previous) => previous + 1)}
    />
  );
}

function SummaryContent({
  report,
  answers,
  restart,
  alwaysExpanded,
}: {
  report: NewReport;
  answers: QuestionWithAnswer[];
  restart: () => void;
  alwaysExpanded: boolean;
}) {
  const { t } = useTranslation('newReport');
  const { addBeautifulToast } = useToast();
  const ref = useRef<HTMLElement>(null);
  const titleId = useId();
  const contentId = useId();
  const [expansion, setExpansion] = useState({ alwaysExpanded, expanded: false });
  useEffect(() => setExpansion({ alwaysExpanded, expanded: false }), [alwaysExpanded]);
  const expanded = alwaysExpanded || (expansion.alwaysExpanded === alwaysExpanded && expansion.expanded);
  const setExpanded = (value: boolean) => setExpansion({ alwaysExpanded, expanded: value });
  const [refreshing, setRefreshing] = useState(false);
  const refreshLock = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [clearCache] = useClearAiSurveySummaryCacheMutation();
  const { source, text, generating, error, streamed } = useSummaryStream(
    report.accessToken,
    report.languageCode,
    answers,
  );
  const availableIndexes = useMemo(() => new Set(answers.map((answer) => answer.id)), [answers]);
  const points = useMemo(() => parseSummaryPoints(text, availableIndexes), [text, availableIndexes]);
  const failed = !!error || (!!source?.done && !source.hash) || (!!source?.done && !generating && !points.length);
  const toastSent = useRef(false);
  useEffect(() => {
    if (generating || failed || !streamed || toastSent.current) return;
    toastSent.current = true;
    addBeautifulToast({
      icon: <RoundCheckedIcon />,
      message: t('SurveySummary:toastMessageDone'),
      buttonText: t('SurveySummary:toastButtonText'),
      duration: 15000,
      buttonOnClick: () => {
        if (!mounted.current) return;
        setExpansion({ alwaysExpanded, expanded: true });
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
    });
  }, [generating, failed, streamed, addBeautifulToast, t, alwaysExpanded]);

  const regenerate = async () => {
    if (!source?.hash || generating || refreshLock.current) return;
    refreshLock.current = true;
    setRefreshing(true);
    try {
      const result = await clearCache({ variables: { accessToken: report.accessToken, hash: source.hash } });
      if (!mounted.current) return;
      const status = result.data?.clearAiSurveySummaryCache;
      if (status?.limitReached) {
        addBeautifulToast({
          message: t('SurveySummary:refreshLimitReached'),
          icon: <ErrorWarningIcon />,
          duration: 10000,
        });
      } else if (status) {
        // An expired/missing cache needs a fresh request too, not a silent no-op.
        restart();
      } else {
        throw new Error('No cache-clear result');
      }
    } catch {
      if (mounted.current) addBeautifulToast({ message: t('aiSummary.generationFailed'), icon: <ErrorWarningIcon /> });
    } finally {
      refreshLock.current = false;
      if (mounted.current) setRefreshing(false);
    }
  };
  const toggle = () => {
    if (!expanded) {
      const userId = auth.getUserId();
      pushToDataLayer({
        event: 'ai_summary_show_more',
        hit: {
          event_type: 'generic_event',
          action_name: 'show_more',
          report_version: 'new',
          ...(userId && { user_id: userId }),
        },
        _clear: true,
      });
    }
    setExpanded(!expanded);
  };

  return (
    <Card
      ref={ref}
      aria-labelledby={titleId}
      data-cy="new-report-ai-summary"
      data-complete-ai-report-summary={!generating && !failed}
    >
      <Header>
        <AiBadge aria-hidden="true">AI</AiBadge>
        <HeaderBody>
          <TitleRow>
            <h2 id={titleId}>{t('SurveySummary:title')}</h2>
            {SUMMARY_SENTIMENTS.map((sentiment) => {
              const count = points.filter((point) => point.sentiment === sentiment).length;
              return count ? (
                <CountBadge key={sentiment} sentiment={sentiment}>
                  {t(`aiSummary.counts.${sentiment}`, { count, postProcess: 'interval' })}
                </CountBadge>
              ) : null;
            })}
          </TitleRow>
          <p>{t('SurveySummary:description')}</p>
        </HeaderBody>
        <RefreshButton
          type="button"
          title={t('SurveySummary:refreshTooltip')}
          aria-label={t('SurveySummary:refreshTooltip')}
          disabled={generating || refreshing || failed || !source?.hash}
          onClick={regenerate}
          data-icom="new-report-ai-summary-regenerate"
          data-cy="ai-summary-regenerate"
          data-pptx-hide
        >
          <Refresh aria-hidden="true" />
        </RefreshButton>
      </Header>
      <Body>
        {failed ? (
          <div role="alert">
            <p>{t('aiSummary.generationFailed')}</p>
            <ToggleButton type="button" onClick={restart}>
              {t('aiSummary.retry')}
            </ToggleButton>
          </div>
        ) : (
          <>
            {(generating || refreshing) && <AiSummaryLoading />}
            {!!points.length && (
              <>
                <div id={contentId}>
                  {expanded ? (
                    <SummaryPoints report={report} points={points} />
                  ) : (
                    <Preview aria-hidden="true">
                      <SummaryPoints report={report} points={points} preview />
                    </Preview>
                  )}
                </div>
                {!alwaysExpanded && (
                  <Footer data-pptx-hide>
                    <ToggleButton
                      type="button"
                      onClick={toggle}
                      aria-expanded={expanded}
                      aria-controls={contentId}
                      data-icom={expanded ? 'new-report-ai-summary-show-less' : 'new-report-ai-summary-show-more'}
                      data-cy="ai-summary-toggle"
                    >
                      {t(expanded ? 'table.showLess' : 'table.showMore')}
                    </ToggleButton>
                  </Footer>
                )}
              </>
            )}
          </>
        )}
      </Body>
    </Card>
  );
}
